import React, { useEffect, useState } from 'react';
import api from '../services/api';
import '../styles/VendorEarnings.css';

interface Transaction {
  _id: string;
  type: string;
  amount: number;
  description: string;
  driver: {
    firstName: string;
    lastName: string;
    phone: string;
  };
  jobId?: {
    serviceType: string;
    estimatedPrice: number;
  };
  status: string;
  createdAt: string;
}

interface EarningsSummary {
  totalEarnings: number;
  totalWithdrawals: number;
  availableBalance: number;
  byType: {
    [key: string]: {
      total: number;
      count: number;
    };
  };
}

const VendorEarnings: React.FC = () => {
  const [summary, setSummary] = useState<EarningsSummary | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchEarnings();
  }, []);

  const fetchEarnings = async () => {
    setIsLoading(true);
    try {
      const data = await api.getVendorEarnings({ limit: 100 });
      setSummary(data.summary);
      setTransactions(data.transactions);
      setError('');
    } catch (err: any) {
      setError('Failed to load earnings data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `₹${Math.abs(amount).toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'job_earning':
        return '💰';
      case 'cash_payment':
        return '💵';
      case 'withdrawal':
        return '🏦';
      case 'bonus':
        return '🎁';
      case 'refund':
        return '↩️';
      default:
        return '💳';
    }
  };

  const getTransactionColor = (type: string) => {
    if (type === 'withdrawal') return 'negative';
    return 'positive';
  };

  if (isLoading) {
    return (
      <div className="vendor-earnings-page">
        <div className="loading">Loading earnings...</div>
      </div>
    );
  }

  return (
    <div className="vendor-earnings-page">
      <div className="page-header">
        <div>
          <h1>Earnings</h1>
          <p>Track your fleet earnings and transactions</p>
        </div>
        <button className="refresh-button" onClick={fetchEarnings}>
          🔄 Refresh
        </button>
      </div>

      {error && <div className="error-box">{error}</div>}

      {/* Summary Cards */}
      <div className="summary-grid">
        <div className="summary-card total-earnings">
          <div className="summary-icon">💰</div>
          <div className="summary-content">
            <h3>Total Earnings</h3>
            <div className="summary-value">{formatCurrency(summary?.totalEarnings || 0)}</div>
            <div className="summary-subtitle">From all completed jobs</div>
          </div>
        </div>

        <div className="summary-card total-withdrawals">
          <div className="summary-icon">🏦</div>
          <div className="summary-content">
            <h3>Total Withdrawals</h3>
            <div className="summary-value">{formatCurrency(summary?.totalWithdrawals || 0)}</div>
            <div className="summary-subtitle">Amount withdrawn</div>
          </div>
        </div>

        <div className="summary-card available-balance">
          <div className="summary-icon">💵</div>
          <div className="summary-content">
            <h3>Available Balance</h3>
            <div className="summary-value">{formatCurrency(summary?.availableBalance || 0)}</div>
            <div className="summary-subtitle">Ready for withdrawal</div>
          </div>
        </div>
      </div>

      {/* Transaction Breakdown */}
      {summary?.byType && Object.keys(summary.byType).length > 0 && (
        <div className="breakdown-section">
          <h2>Transaction Breakdown</h2>
          <div className="breakdown-grid">
            {Object.entries(summary.byType).map(([type, data]) => (
              <div key={type} className="breakdown-card">
                <div className="breakdown-type">{type.replace(/_/g, ' ').toUpperCase()}</div>
                <div className="breakdown-amount">{formatCurrency(data.total)}</div>
                <div className="breakdown-count">{data.count} transactions</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transaction History */}
      <div className="transactions-section">
        <h2>Transaction History</h2>

        {/* Desktop Table */}
        <div className="desktop-table-container">
          <div className="table-container">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Driver</th>
                  <th>Status</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((txn) => (
                  <tr key={txn._id}>
                    <td>{formatDate(txn.createdAt)}</td>
                    <td>
                      <span className="transaction-type">
                        {getTransactionIcon(txn.type)} {txn.type.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>{txn.description}</td>
                    <td>
                      {txn.driver && (
                        <>
                          <div>{txn.driver.firstName} {txn.driver.lastName}</div>
                          <div className="secondary-text">{txn.driver.phone}</div>
                        </>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${txn.status.toLowerCase()}`}>
                        {txn.status}
                      </span>
                    </td>
                    <td>
                      <span className={`amount ${getTransactionColor(txn.type)}`}>
                        {txn.type === 'withdrawal' ? '-' : '+'}{formatCurrency(txn.amount)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {transactions.length === 0 && (
              <div className="empty-state">
                <p>No transactions yet</p>
                <p className="empty-subtitle">Earnings will appear here once jobs are completed</p>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Cards */}
        <div className="mobile-card-list">
          {transactions.map((txn) => (
            <div key={txn._id} className="data-card">
              <div className="data-card-header">
                <div>
                  <div className="data-card-title">
                    {getTransactionIcon(txn.type)} {txn.type.replace(/_/g, ' ')}
                  </div>
                  <div className="data-card-subtitle">{formatDate(txn.createdAt)}</div>
                </div>
                <div className={`amount-large ${getTransactionColor(txn.type)}`}>
                  {txn.type === 'withdrawal' ? '-' : '+'}{formatCurrency(txn.amount)}
                </div>
              </div>

              <div className="data-card-body">
                <div className="data-card-row">
                  <span className="data-card-label">Description:</span>
                  <span className="data-card-value">{txn.description}</span>
                </div>
                {txn.driver && (
                  <>
                    <div className="data-card-row">
                      <span className="data-card-label">Driver:</span>
                      <span className="data-card-value">
                        {txn.driver.firstName} {txn.driver.lastName}
                      </span>
                    </div>
                    <div className="data-card-row">
                      <span className="data-card-label">Phone:</span>
                      <span className="data-card-value">{txn.driver.phone}</span>
                    </div>
                  </>
                )}
                <div className="data-card-row">
                  <span className="data-card-label">Status:</span>
                  <span className={`status-badge ${txn.status.toLowerCase()}`}>
                    {txn.status}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {transactions.length === 0 && (
            <div className="empty-state">
              <p>No transactions yet</p>
              <p className="empty-subtitle">Earnings will appear here once jobs are completed</p>
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="info-box">
        <h3>💡 About Earnings</h3>
        <ul>
          <li>All jobs completed by your drivers contribute to your total earnings</li>
          <li>Earnings are calculated after platform commission is deducted</li>
          <li>Drivers in your fleet do not have individual wallets - all payments go to you</li>
          <li>Contact admin for withdrawal requests and payment processing</li>
        </ul>
      </div>
    </div>
  );
};

export default VendorEarnings;
